/**
 * Winess collection filter — client-side filtering with cross-page support.
 *
 * Why client-side: Shopify Liquid doesn't reliably expose query strings
 * (`request.url` returns nil) and Search & Discovery isn't configured for
 * tag filters, so we can't filter server-side. Doing it in JS removes both
 * blockers.
 *
 * How it works:
 *   1. Server renders all products on the current paginated page with each
 *      product's tags exposed via `data-winess-product-tags`.
 *   2. On load, we read `?w_filter=tag1,tag2,...` from the URL.
 *   3. To handle catalogues larger than Shopify's 50-per-page paginate cap,
 *      we AJAX-fetch every other paginated page in parallel, harvest the
 *      product cards, and append them to the grid (hidden until filtered).
 *   4. We hide products whose tags don't match the active filter (OR within
 *      group, AND across groups).
 *   5. Checkbox/chip changes update the URL via pushState and re-apply the
 *      filter — no page reload.
 *   6. Browser back/forward restores filter state via popstate.
 *
 *  Design notes:
 *    - All custom selectors are namespaced `data-winess-*` so they don't
 *      clash with Craft's own filter widgets if they're ever re-enabled.
 *    - No external dependencies (no jQuery, no fetch polyfills — modern
 *      browser support is fine, IE is dead).
 *    - The script is idempotent: safe to load multiple times.
 */
(function () {
  'use strict';

  var URL_PARAM = 'w_filter';

  // ── Helpers ─────────────────────────────────────────────────────────────

  function readGroupConfig() {
    var node = document.querySelector('[data-winess-filter-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      return {};
    }
  }

  function readActiveTagsFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var raw = params.get(URL_PARAM) || '';
    return raw
      .split(',')
      .map(function (s) { return s.trim().toLowerCase(); })
      .filter(Boolean);
  }

  function groupActiveTags(activeTags, groupConfig) {
    // { "Range": ["arka","mod"], "Temperature": ["single-temp"], ... }
    var result = {};
    Object.keys(groupConfig).forEach(function (groupName) {
      var groupTags = (groupConfig[groupName] || []).map(function (t) { return t.toLowerCase(); });
      var hits = activeTags.filter(function (t) { return groupTags.indexOf(t) !== -1; });
      if (hits.length) result[groupName] = hits;
    });
    return result;
  }

  function getProductTags(itemEl) {
    var raw = (itemEl.getAttribute('data-winess-product-tags') || '').toLowerCase();
    return raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function productMatches(productTags, activeByGroup) {
    var groupNames = Object.keys(activeByGroup);
    for (var i = 0; i < groupNames.length; i++) {
      var groupActive = activeByGroup[groupNames[i]];
      var hit = false;
      for (var j = 0; j < groupActive.length; j++) {
        if (productTags.indexOf(groupActive[j]) !== -1) { hit = true; break; }
      }
      if (!hit) return false; // AND across groups
    }
    return true;
  }

  function tagLabelFor(tag) {
    var input = document.querySelector(
      'input[data-winess-tag][value="' + cssEscape(tag) + '"]'
    );
    if (input && input.dataset.winessLabel) return input.dataset.winessLabel;
    return tag.replace(/-/g, ' ').replace(/^./, function (c) { return c.toUpperCase(); });
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }
    return String(value).replace(/[^a-zA-Z0-9_-]/g, function (c) { return '\\' + c; });
  }

  // ── Cross-page prefetch ─────────────────────────────────────────────────
  // Shopify caps paginate at 50 items per page. For catalogues larger than
  // that, we need to fetch the other paginated pages so the client-side
  // filter sees every product, not just the current page.

  function prefetchOtherPages(grid) {
    var paginationLinks = document.querySelectorAll('.pagination__list a[href]');
    if (!paginationLinks.length) return Promise.resolve();

    // Find the maximum page number reachable. Pagination may collapse middle
    // pages behind "…" (e.g. "1 [2] [3] … [6]"), so we read the highest page
    // number from any link and fetch every page 1..max we don't already have.
    var currentPath = window.location.pathname;
    var currentPage = parseInt((new URLSearchParams(window.location.search)).get('page') || '1', 10);
    var maxPage = currentPage;
    paginationLinks.forEach(function (a) {
      try {
        var u = new URL(a.href, window.location.origin);
        if (u.pathname !== currentPath) return;
        var p = parseInt(u.searchParams.get('page') || '1', 10);
        if (!isNaN(p) && p > maxPage) maxPage = p;
      } catch (e) {}
    });
    var pages = new Set();
    for (var i = 1; i <= maxPage; i++) {
      if (i !== currentPage) pages.add(String(i));
    }
    if (pages.size === 0) return Promise.resolve();

    var fetches = Array.from(pages).map(function (page) {
      var u = new URL(window.location.href);
      u.searchParams.set('page', page);
      u.searchParams.delete(URL_PARAM); // fetch the unfiltered page
      return fetch(u.pathname + u.search, { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var items = doc.querySelectorAll('[data-winess-product-tags]');
          items.forEach(function (item) {
            // Tag prefetched items so we can sort or remove later if needed
            item.setAttribute('data-winess-prefetched', page);
            grid.appendChild(item);
          });
        })
        .catch(function () { /* fail-soft: filter still works on current page */ });
    });

    return Promise.all(fetches);
  }

  // ── Render: filter, count, chips, clear-all ─────────────────────────────

  function applyFilter(state) {
    var activeByGroup = groupActiveTags(state.activeTags, state.groupConfig);
    var items = document.querySelectorAll('[data-winess-product-tags]');
    var visible = 0;
    var total = items.length;

    items.forEach(function (item) {
      var match = productMatches(getProductTags(item), activeByGroup);
      if (match) {
        item.removeAttribute('hidden');
        visible++;
      } else {
        item.setAttribute('hidden', '');
      }
    });

    updateCount(visible, total, state.activeTags.length);
    updateEmptyState(visible);
    hydrateCheckboxes(state.activeTags);
    renderActiveChips(state.activeTags);
    updateClearAll(state.activeTags);
    updateSearchAllLink(state.activeTags);
    hidePaginationWhenFiltering(state.activeTags.length > 0);
  }

  function updateCount(visible, total, activeCount) {
    var node = document.querySelector('[data-winess-result-count]');
    if (!node) return;
    var unit = visible === 1 ? 'product' : 'products';
    if (activeCount > 0 && visible !== total) {
      node.textContent = visible + ' of ' + total + ' ' + unit;
    } else {
      node.textContent = total + (total === 1 ? ' product' : ' products');
    }
  }

  function updateEmptyState(visible) {
    var empty = document.querySelector('[data-winess-empty-state]');
    if (!empty) return;
    if (visible === 0) empty.removeAttribute('hidden');
    else empty.setAttribute('hidden', '');
  }

  function hydrateCheckboxes(activeTags) {
    document.querySelectorAll('input[data-winess-tag]').forEach(function (cb) {
      cb.checked = activeTags.indexOf(cb.value.toLowerCase()) !== -1;
    });
  }

  function renderActiveChips(activeTags) {
    var list = document.querySelector('[data-winess-active-chips]');
    if (!list) return;
    list.innerHTML = '';
    if (activeTags.length === 0) {
      list.setAttribute('hidden', '');
      return;
    }
    list.removeAttribute('hidden');
    activeTags.forEach(function (tag) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'winess-filters__active-chip';
      btn.setAttribute('data-winess-remove-tag', tag);
      btn.setAttribute('aria-label', 'Remove filter ' + tagLabelFor(tag));

      var labelSpan = document.createElement('span');
      labelSpan.textContent = tagLabelFor(tag);
      var x = document.createElement('span');
      x.setAttribute('aria-hidden', 'true');
      x.textContent = '×';

      btn.appendChild(labelSpan);
      btn.appendChild(x);
      li.appendChild(btn);
      list.appendChild(li);
    });
  }

  function updateClearAll(activeTags) {
    var link = document.querySelector('[data-winess-clear-all]');
    if (!link) return;
    if (activeTags.length === 0) link.setAttribute('hidden', '');
    else link.removeAttribute('hidden');
  }

  function updateSearchAllLink(activeTags) {
    var link = document.querySelector('[data-winess-search-all]');
    if (!link) return;
    var u = new URL(link.href, window.location.origin);
    if (activeTags.length === 0) {
      u.searchParams.delete(URL_PARAM);
    } else {
      u.searchParams.set(URL_PARAM, activeTags.join(','));
    }
    link.setAttribute('href', u.pathname + u.search);
  }

  function hidePaginationWhenFiltering(filtering) {
    // While a filter is active we've prefetched all pages into one grid, so
    // the server-rendered pagination is misleading.
    var pag = document.querySelector('.pagination-wrapper');
    if (!pag) return;
    if (filtering) pag.setAttribute('hidden', '');
    else pag.removeAttribute('hidden');
  }

  // ── URL state ───────────────────────────────────────────────────────────

  function writeUrl(activeTags) {
    var url = new URL(window.location.href);
    if (activeTags.length) {
      url.searchParams.set(URL_PARAM, activeTags.join(','));
      url.searchParams.delete('page'); // always start from page 1
    } else {
      url.searchParams.delete(URL_PARAM);
    }
    window.history.pushState({}, '', url.pathname + url.search + url.hash);
  }

  function setActiveTags(state, nextTags) {
    state.activeTags = nextTags;
    writeUrl(nextTags);
    applyFilter(state);
  }

  // ── Init ────────────────────────────────────────────────────────────────

  function init() {
    var sidebar = document.querySelector('[data-winess-filters]');
    if (!sidebar || sidebar.dataset.winessInitialised === '1') return;
    sidebar.dataset.winessInitialised = '1';

    var grid = document.querySelector('[data-winess-grid], #product-grid');
    var state = {
      groupConfig: readGroupConfig(),
      activeTags: readActiveTagsFromUrl(),
    };

    // Apply current state immediately, before prefetch finishes.
    applyFilter(state);

    if (grid) {
      prefetchOtherPages(grid).then(function () {
        applyFilter(state); // re-apply now that more products are in the DOM
      });
    }

    // Checkbox toggle
    sidebar.addEventListener('change', function (e) {
      if (!e.target || !e.target.matches || !e.target.matches('input[data-winess-tag]')) return;
      var tag = e.target.value.toLowerCase();
      var next = state.activeTags.slice();
      if (e.target.checked) {
        if (next.indexOf(tag) === -1) next.push(tag);
      } else {
        next = next.filter(function (t) { return t !== tag; });
      }
      setActiveTags(state, next);
    });

    // Chip remove
    sidebar.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[data-winess-remove-tag]');
      if (!btn) return;
      e.preventDefault();
      var tag = btn.getAttribute('data-winess-remove-tag').toLowerCase();
      setActiveTags(state, state.activeTags.filter(function (t) { return t !== tag; }));
    });

    // Clear all (sidebar link)
    sidebar.addEventListener('click', function (e) {
      var link = e.target && e.target.closest && e.target.closest('[data-winess-clear-all]');
      if (!link) return;
      e.preventDefault();
      setActiveTags(state, []);
    });

    // Clear all (link inside empty state, which sits in the section, not sidebar)
    document.addEventListener('click', function (e) {
      var link = e.target && e.target.closest && e.target.closest('[data-winess-clear-all-link]');
      if (!link) return;
      e.preventDefault();
      setActiveTags(state, []);
    });

    // Browser back/forward
    window.addEventListener('popstate', function () {
      state.activeTags = readActiveTagsFromUrl();
      applyFilter(state);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
